import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { productionConfig } from '../../config/production.js';

// Create a completely standalone API client for payment operations
// This bypasses all authentication and interceptors to prevent login redirects
const createPaymentClient = () => {
  const client = axios.create({
    baseURL: 'https://app.insight360apps.com/easycheck/api',
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // Minimal logging only - no authentication logic
  client.interceptors.request.use(
    (config) => {
      console.log(`💳 Payment Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
      return config;
    }
  );

  client.interceptors.response.use(
    (response) => {
      console.log(`✅ Payment Response: ${response.status} ${response.statusText}`);
      return response;
    },
    (error) => {
      console.error('❌ Payment Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        message: error.message
      });
      
      // CRITICAL: Prevent any automatic redirects
      if (error.response?.status === 401) {
        console.error('🚨 401 UNAUTHORIZED in payment client - BUT NOT REDIRECTING!');
        console.error('🚨 This should not cause a login redirect');
      }
      
      // Never redirect - just return the error
      return Promise.reject(error);
    }
  );

  return client;
};

const paymentApi = createPaymentClient();

// Initialize Stripe - get publishable key from environment with validation
const getStripeKey = () => {
  console.log('🔍 Debug: All environment variables:', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
    REACT_APP_STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? 'EXISTS' : 'NOT_FOUND',
    REACT_APP_APP_NAME: process.env.REACT_APP_APP_NAME
  });
  
  console.log('🔍 Raw Stripe key value:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  console.log('🔍 Window location:', window.location.origin);
  console.log('🔍 Is production?', process.env.NODE_ENV === 'production');
  
  // Use production config fallback
  const key = productionConfig.get('STRIPE_PUBLISHABLE_KEY');
  
  console.log('🔍 Key from production config:', key ? key.substring(0, 20) + '...' : 'NOT_FOUND');
  
  if (!key || key === 'pk_test_YOUR_ACTUAL_STRIPE_PUBLISHABLE_KEY_HERE') {
    console.warn('⚠️ REACT_APP_STRIPE_PUBLISHABLE_KEY not found in environment variables or production config');
    console.warn('⚠️ Current value:', key);
    console.warn('⚠️ Please set your actual Stripe key in .env file or hosting platform environment variables');
    return 'pk_test_placeholder'; // Placeholder to prevent undefined error
  }
  console.log('✅ Stripe key loaded:', key.substring(0, 20) + '...');
  return key;
};

const stripePromise = loadStripe(getStripeKey());

export const stripeService = {
  // Create payment session for signup
  createSignupPaymentSession: async (signupData, planDetails = null) => {
    try {
      // Validate Stripe initialization
      const stripeKey = getStripeKey();
      if (!stripeKey || stripeKey === 'pk_test_placeholder') {
        throw new Error('Stripe publishable key not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file or hosting platform environment variables');
      }

      // Default plan if not provided
      const plan = planDetails || {
        planId: 'business',
        planName: 'Business',
        amount: 75000, // 750 AED in fils
        currency: 'aed'
      };

      console.log('🔄 Creating Stripe payment session for signup...');
      console.log('🔄 Request data:', {
        signupData: { ...signupData, password: '[HIDDEN]' },
        plan: plan
      });

      const response = await paymentApi.post('/payment/create-signup-session', {
        signupData,
        planId: plan.planId,
        planName: plan.planName,
        amount: plan.amount,
        currency: plan.currency,
        successUrl: `${window.location.origin}/signup/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/signup/payment-cancelled`
      });

      console.log('✅ Payment session created:', response.data);
      
      // Handle the actual API response structure
      const apiResponse = response.data;
      
      if (apiResponse.Success && apiResponse.Data) {
        return {
          success: true,
          sessionId: apiResponse.Data.SessionId,
          sessionUrl: apiResponse.Data.SessionUrl,
          publishableKey: apiResponse.Data.PublishableKey
        };
      } else {
        // Fallback for different response structure
        return {
          success: false,
          message: apiResponse.Message || 'Payment session creation failed'
        };
      }
    } catch (error) {
      console.error('❌ Error creating payment session:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Extract proper string error message from API response structure
      let errorMessage = 'Failed to create payment session';
      
      if (error.response?.data) {
        const apiResponse = error.response.data;
        
        // Handle API response structure: {Success: false, Message: "...", ErrorDetails: "..."}
        if (apiResponse.Message && typeof apiResponse.Message === 'string') {
          errorMessage = apiResponse.Message;
        } else if (apiResponse.ErrorDetails && typeof apiResponse.ErrorDetails === 'string') {
          errorMessage = apiResponse.ErrorDetails;
        } else if (typeof apiResponse === 'string') {
          errorMessage = apiResponse;
        } else if (apiResponse.message && typeof apiResponse.message === 'string') {
          errorMessage = apiResponse.message;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  // Verify payment status
  verifyPayment: async (sessionId) => {
    try {
      console.log('🔄 Verifying payment status...', sessionId);
      
      const response = await paymentApi.get(`/payment/verify-session/${sessionId}`);
      
      console.log('✅ Payment verification response:', response.data);
      console.log('✅ PRODUCTION DEBUG: Processing verification response...');
      
      // Handle the actual API response structure
      const apiResponse = response.data;
      
      if (apiResponse.Success && apiResponse.Data) {
        const status = apiResponse.Data.Status;
        console.log('🔍 Raw status from API:', status, 'Type:', typeof status);
        
        const result = {
          success: true,
          status: status, // Extract from Data.Status
          customerEmail: apiResponse.Data.CustomerEmail,
          paymentIntentId: apiResponse.Data.PaymentIntentId
        };
        
        console.log('🔍 PRODUCTION DEBUG: Returning verification result:', result);
        return result;
      } else {
        console.error('❌ API response structure issue:', {
          Success: apiResponse.Success,
          hasData: !!apiResponse.Data,
          fullResponse: apiResponse
        });
        return {
          success: false,
          message: apiResponse.Message || 'Payment verification failed'
        };
      }
    } catch (error) {
      console.error('❌ Error verifying payment:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // Extract proper string error message from API response structure
      let errorMessage = 'Failed to verify payment';
      
      if (error.response?.data) {
        const apiResponse = error.response.data;
        
        if (apiResponse.Message && typeof apiResponse.Message === 'string') {
          errorMessage = apiResponse.Message;
        } else if (apiResponse.ErrorDetails && typeof apiResponse.ErrorDetails === 'string') {
          errorMessage = apiResponse.ErrorDetails;
        }
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  // Complete signup after successful payment
  completeSignupAfterPayment: async (sessionId, signupData) => {
    try {
      console.log('🔄 Completing signup after payment...', sessionId);
      console.log('🔄 Signup data being sent:', signupData);
      
      const requestBody = {
        SessionId: sessionId,  // Capital S to match VB.NET controller
        SignupData: signupData  // Capital S to match VB.NET controller
      };
      
      console.log('🔄 Complete request body:', requestBody);
      
      const response = await paymentApi.post('/payment/complete-signup', requestBody);

      console.log('✅ Signup completed:', response.data);
      console.log('✅ Response status:', response.status);
      console.log('✅ Response headers:', response.headers);
      
      // Handle the actual API response structure
      const apiResponse = response.data;
      
      if (apiResponse.Success) {
        return {
          success: true,
          user: apiResponse.Data?.user || apiResponse.Data,
          message: apiResponse.Message || 'Signup completed successfully'
        };
      } else {
        return {
          success: false,
          message: apiResponse.Message || apiResponse.ErrorDetails || 'Failed to complete signup'
        };
      }
    } catch (error) {
      console.error('❌ Error completing signup:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // Extract proper string error message from API response structure
      let errorMessage = 'Failed to complete signup';
      
      if (error.response?.data) {
        const apiResponse = error.response.data;
        
        if (apiResponse.Message && typeof apiResponse.Message === 'string') {
          errorMessage = apiResponse.Message;
        } else if (apiResponse.ErrorDetails && typeof apiResponse.ErrorDetails === 'string') {
          errorMessage = apiResponse.ErrorDetails;
        } else if (typeof apiResponse === 'string') {
          errorMessage = apiResponse;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  // Validate payment code for direct billing customers
  validatePaymentCode: async ({ email, paymentCode, signupData, planId, planName }) => {
    try {
      console.log('🎫 Validating payment code:', { email, paymentCode: paymentCode.substring(0, 4) + '***', planId });

      const response = await paymentApi.post('/payment/validate-payment-code', {
        email,
        paymentCode,
        signupData,
        planId: planId || 'business',
        planName: planName || 'Business'
      });

      console.log('✅ Payment code validation response:', response.data);
      
      // Handle the actual API response structure
      const apiResponse = response.data;
      
      if (apiResponse.Success && apiResponse.Data?.IsValid) {
        return {
          success: true,
          message: apiResponse.Message || 'Payment code validated successfully',
          userData: apiResponse.Data.UserData
        };
      } else {
        return {
          success: false,
          message: apiResponse.Message || apiResponse.ErrorDetails || 'Invalid payment code'
        };
      }
    } catch (error) {
      console.error('❌ Error validating payment code:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Extract proper string error message from API response structure
      let errorMessage = 'Failed to validate payment code';
      
      if (error.response?.data) {
        const apiResponse = error.response.data;
        
        if (apiResponse.Message && typeof apiResponse.Message === 'string') {
          errorMessage = apiResponse.Message;
        } else if (apiResponse.ErrorDetails && typeof apiResponse.ErrorDetails === 'string') {
          errorMessage = apiResponse.ErrorDetails;
        } else if (typeof apiResponse === 'string') {
          errorMessage = apiResponse;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  // Fire-and-forget lead notification when user reaches plan selection
  notifyLead: async (data) => {
    try {
      await paymentApi.post('/payment/notify-lead', data);
    } catch {
      // Silent — must not block signup flow
    }
  },

  // Get Stripe instance
  getStripe: () => stripePromise,

  // Get available pricing plans
  getAvailablePlans: async () => {
    try {
      console.log('📋 Fetching available plans...');
      const response = await paymentApi.get('/payment/plans');
      const apiResponse = response.data;

      if (apiResponse.Success && apiResponse.Data) {
        return {
          success: true,
          plans: apiResponse.Data
        };
      }
      return {
        success: false,
        message: apiResponse.Message || 'Failed to fetch plans'
      };
    } catch (error) {
      console.error('❌ Error fetching plans:', error);
      return {
        success: false,
        message: 'Failed to fetch pricing plans'
      };
    }
  }
}; 